import React, { useState, useEffect, KeyboardEventHandler } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  TextField,
  Button,
  Box,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { RootState } from "../../reducer";
import { ActionStatus } from "../../constants/api";
import { VERIFY_OTP } from "../../redux/login/type";

export interface Props {
  children?: React.ReactNode;
  [name: string]: any;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const LoginPage: React.FC<Props> = ({ name }: Props) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [apiError, setApiError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const VERIFY_OTP_STATUS: string = useSelector<RootState, ActionStatus>(
    (state) => state.login.VERIFY_OTP_STATUS
  );

  const LOGIN_ERROR_MESSAGE = useSelector<RootState, any>(
    (state) => state.login.errorMessage
  );

  const token: any = useSelector<any>((state) => state.login.verificationToken);

  useEffect(() => {
    if (VERIFY_OTP_STATUS === "SUCCESS") {
      // Use lodash isEmpty to check if token exists and is not empty
      if (!_.isEmpty(token)) {
        localStorage.setItem("verificationToken", token);
        localStorage.setItem("otpAuthenticated", "true");
        setSnackbarMessage("Successful!");
        setOpenSnackbar(true);
        setTimeout(() => {
          navigate("/home");
        }, 1000);
      }
    } else if (VERIFY_OTP_STATUS === "FAILURE") {
      // Use lodash isEmpty to check if error message exists
      const errorMsg = _.isEmpty(LOGIN_ERROR_MESSAGE?.error)
        ? "Invalid. Please try again."
        : LOGIN_ERROR_MESSAGE.error;
      setApiError(errorMsg);
    }
  }, [VERIFY_OTP_STATUS, navigate, LOGIN_ERROR_MESSAGE, token]);

  const handleLogin = () => {
    // Reset errors
    setUsernameError("");
    setPasswordError("");
    setApiError("");

    // Validate inputs using lodash isEmpty
    let hasError = false;

    if (_.isEmpty(username?.trim())) {
      setUsernameError("required.");
      hasError = true;
    }

    if (_.isEmpty(password?.trim())) {
      setPasswordError("required.");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      dispatch(VERIFY_OTP.action({ userId: username, password: password }));
    } catch (error) {
      setApiError("failed. Please try again.");
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove all spaces from the username
    const value = e.target.value.replace(/\s/g, "");
    setUsername(value);

    // Clear errors when user starts typing - using lodash isEmpty
    if (!_.isEmpty(usernameError) || !_.isEmpty(apiError)) {
      setUsernameError("");
      setApiError("");
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value.replace(/\s/g, ""); // Remove spaces
    setPassword(newPassword);

    // Clear errors when user starts typing - using lodash isEmpty
    if (!_.isEmpty(passwordError) || !_.isEmpty(apiError)) {
      setPasswordError("");
      setApiError("");
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLFormElement> = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin();
    }
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: "#1064B2" }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            News
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xs">
        <Box
          sx={{
            mt: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5">
            Latest News
          </Typography>
          <Box
            component="form"
            noValidate
            onKeyDown={handleKeyDown}
            sx={{
              mt: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Search"
              name="username"
              autoComplete="new-password"
              autoFocus
              value={username}
              error={!_.isEmpty(usernameError)}
              helperText={usernameError}
              onChange={handleUsernameChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Topic"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              error={!_.isEmpty(passwordError)}
              helperText={passwordError}
              onChange={handlePasswordChange}
            />
            {!_.isEmpty(apiError) && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: 1, textAlign: "center" }}
              >
                {apiError}
              </Typography>
            )}
            <Button
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                width: "auto",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "120px",
              }}
              onClick={handleLogin}
              disabled={VERIFY_OTP_STATUS === "LOADING"}
            >
              {VERIFY_OTP_STATUS === "LOADING" && (
                <CircularProgress
                  size={24}
                  color="inherit"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
              {VERIFY_OTP_STATUS !== "LOADING" && "View News"}
            </Button>
          </Box>
        </Box>
      </Container>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoginPage;
